import {logger, Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {cn} from '@containers/html-utils'
import {UIRegistry} from './ui-registry'

type MountListener = (el: HTMLElement | undefined) => void

/**
 * @name=ui variable
 * @code
 * UI.text('...');
 * @description
 * Represents element of user interface.
 * @descriptionRu
 * Переменная, представляющая элемент пользовательского интерфейса.
 * @example
 * var task = Items.get('...');
 * var ui1 = task.ui; // task’s ui
 *
 * var ui2 = UI.components.chat; // ui for chat
 *
 * // creating new ui
 * var ui3 = UI.text('Hello');
 * // adding it to another ui
 * ui1.add(ui3);
 */
export class UIElement {
  protected uiTag = 'div'
  protected uiId: string
  protected companyId: DataObjectId
  protected uiStyle: {[key: string]: string}
  protected uiClassName: string
  protected uiDataId: DataObjectId | undefined
  protected uiParent: UIElement
  protected uiChildren: UIElement[]
  protected uiShowDefaults: boolean
  protected uiEl: HTMLElement | undefined
  private mountListeners: MountListener[]

  constructor(
    companyId: DataObjectId,
    dataId?: DataObjectId,
    parent?: UIElement
  ) {
    this.companyId = companyId
    this.uiId = Util.UUID()
    this.uiDataId = dataId
    this.uiParent = parent
    this.uiChildren = []
    this.uiShowDefaults = true
    this.uiStyle = {}
    this.mountListeners = []
    if (parent && dataId) {
      throw new Error('UI element can’t have both parent and dataId!')
    }
    const uiReg = UIRegistry.get(this.companyId)
    if (dataId && uiReg.uiRegistry.has(dataId)) {
      throw new Error('UI element already registered!')
    }
    if (dataId) {
      uiReg.uiRegistry.set(dataId, this)
    }
  }

  /**
   * @code
   * Items.get('...').ui.style;
   * @description
   * Object with css styles of the element.
   * @descriptionRu
   * Объект с css-свойствами элемента.
   * @example
   * var txt = UI.text('Ok');
   * txt.style = {
   *   color: '#f00',
   *   fontSize: '18px'
   * };
   */
  public get style(): {[key: string]: string} {
    return this.uiStyle
  }

  public set style(value: {[key: string]: string}) {
    this.uiStyle = Util.clone(value) || {}
    this.refresh()
  }

  public get className(): string {
    return this.uiClassName
  }

  public set className(value: string) {
    this.uiClassName = value
    this.refresh()
  }

  /**
   * @code
   * Items.get('...').ui.add(UI.text(''));
   * @description
   * Adds a new child element to the element.
   * @descriptionRu
   * Добавляет новый дочерний элемент.
   * @example
   * var container = UI.panel();
   * container.add(UI.text('Hello'));
   *
   * Items.get('...').ui.add(container);
   */
  public add(element: UIElement): void {
    element.appendTo(this)
  }

  /**
   * @code
   * UI.text('').appendTo(Items.get('...').ui);
   * @description
   * Adds the element as a child to some other element.
   * @descriptionRu
   * Добавляет элемент внутрь другого элемента.
   */
  public appendTo(uiElement: UIElement): void {
    const hasIx = uiElement.uiChildren.findIndex(
      (el: UIElement) => el.uiId === this.uiId
    )
    if (hasIx > -1) {
      logger.warn('Already added', hasIx, uiElement.uiChildren)
      return
    }
    if (this.uiParent) {
      const oldParent = this.uiParent
      const ix = oldParent.uiChildren.findIndex(
        (el: UIElement) => el.uiId === this.uiId
      )
      if (ix > -1) {
        oldParent.uiChildren.splice(ix, 1)
      }
    }
    this.uiParent = uiElement
    uiElement.uiChildren.push(this)
    this.refresh()
  }

  /**
   * @code
   * UI.text('').getElement()
   * @description
   * Returns HTML element if this ui object was rendered, otherwise
   * returns undefined.
   * @descriptionRu
   * Возвращает HTML элемент текущего UI объекта если он был отрисован,
   * иначе возвращает undefined.
   * @example
   * var component = UI.text("Some text");
   * component.onRender = function () {
   *   var el = component.getElement();
   * };
   */
  public getElement(): Element {
    return this.uiEl
  }

  /**
   * @code
   * UI.text('').query('... css selector');
   * @description
   * Returns an array of HTML elements that match
   * specified css-selector. If the element wasn't
   * rendered then this method returns undefined.
   * Use this method inside onRender function to get
   * meaningful result.
   * @descriptionRu
   * Возвращает массив HTML-элементов, которые
   * подходят под указанный css-селектор. Если элемент
   * не был отрисован на странице, то этот метод вернёт undefined,
   * поэтому его нужно использовать вместе с функцией onRender.
   * @example
   * var component = UI.raw(\`
   *   <div>
   *     <video src="" class="my-video"></video>
   *   </div>
   * \`);
   * component.onRender = function () {
   *   var vidEl = component.query('.my-video')[0];
   * };
   */
  public query(selector: string): Element[] {
    if (!this.uiEl) {
      logger.error(
        'Tried to query element that is not rendered yet',
        selector,
        this
      )
      return []
    }
    return [...Util.iter(this.uiEl.querySelectorAll(selector))]
  }

  public render(el: HTMLElement, rootElement?: HTMLElement): void {
    this.uiEl = el
    if (rootElement) {
      el.innerHTML = ''
    }
    const {parentElement} = el
    if (!parentElement) {
      return
    }
    el.classList.add('ui-cnt')
    if (!this.uiShowDefaults) {
      parentElement.classList.add('ui-hide-defaults')
    } else {
      parentElement.classList.remove('ui-hide-defaults')
    }
    el.id = this.uiId
    if (!rootElement) {
      Object.keys(this.style).forEach((key: string) => {
        ;(el.style as any)[key] = this.style[key]
      })
      el.className = cn(el.className, this.className)
    } else {
      Object.keys(this.style).forEach((key: string) => {
        ;(rootElement.style as any)[key] = this.style[key]
      })
      rootElement.className = cn(rootElement.className, this.className)
    }
    if (this.uiChildren) {
      this.uiChildren.forEach(ch => {
        const chEl = document.createElement(ch.uiTag)
        el.appendChild(chEl)
        ch.render(chEl)
      })
    }
    if (!rootElement) {
      el.onclick = (e: MouseEvent) => {
        if (!this.onClick) {
          return
        }
        const proceed = this.onClick(e)
        if (!proceed) {
          e.stopPropagation()
        }
      }
    } else {
      rootElement.onclick = (e: MouseEvent) => {
        if (!this.onClick) {
          return
        }
        const proceed = this.onClick(e)
        if (!proceed) {
          e.stopPropagation()
        }
      }
    }

    if (this.onRender) {
      this.onRender()
    }

    this.mountListeners.forEach(handler => handler(el))
  }

  /**
   * @code
   * UI.text('').remove();
   * @description
   * Removes element from its parent.
   * @descriptionRu
   * Убирает элемент из своего родительского элемента.
   */
  public remove(): void {
    if (this.uiParent) {
      const ix = this.uiParent.uiChildren.findIndex(
        (el: UIElement) => el.uiId === this.uiId
      )
      this.uiParent.uiChildren.splice(ix, 1)
      this.uiParent.refresh()
    }
  }

  /**
   * @code
   * UI.text('').clear();
   * @description
   * Clears all custom styles and removes custom elements from the element.
   * @descriptionRu
   * Очищает все дополнительные стили и убирает дочерние элементы.
   * @example
   * var task = Items.get('...');
   * var count = 0;
   * setInterval(function () {
   *   count++;
   *   // remove previously added text
   *   task.ui.clean();
   *   task.ui.add(UI.text(count));
   * }, 1000);
   */
  public clear(): void {
    this.uiChildren = []
    this.uiStyle = {}
    this.refresh()
  }

  public clearUiChildren(childrenName: string): void {
    this.uiChildren = this.uiChildren.filter(
      child => child.constructor.name !== childrenName
    )
    this.refresh()
  }

  /**
   * @code
   * UI.text('').showDefaults;
   * @description
   * Tells if default interfaces should be shown.
   * @descriptionRu
   * Если установить в false, то стандартные интерфейсы,
   * связанные с этим элементом не будут отображаться.
   * @example
   */
  public get showDefaults(): boolean {
    return this.uiShowDefaults
  }

  public set showDefaults(val: boolean) {
    this.uiShowDefaults = val
    this.refresh()
  }

  /**
   * @code
   * Items.get('...').ui.parent;
   * @description
   * Returns a parent element
   * @descriptionRu
   * Возврашает родительский элемент.
   */
  public get parent(): UIElement {
    return this.uiParent
  }

  /**
   * @code
   * Items.get('...').ui.children;
   * @description
   * Returns an array all child elements.
   * @descriptionRu
   * Возвращает массив всех дочерних элементов.
   */
  public get children(): UIElement[] {
    if (!this.uiChildren || !this.uiChildren.length) {
      return []
    }
    return [...this.uiChildren]
  }

  /**
   * @todo implement
   */
  public getChildById(): UIElement {
    throw new Error('Unimplemented')
  }

  /**
   * @code
   * Items.get('...').ui.refresh();
   * @description
   * Redraws element.
   * @descriptionRu
   * Перерисовывает элемент.
   */
  public refresh(): void {
    if (this.uiDataId) {
      const uiReg = UIRegistry.get(this.companyId)
      uiReg.refresh(this.uiDataId)
    } else if (this.uiParent) {
      this.uiParent.refresh()
    }
  }

  /**
   * @code
   * Items.get('...').ui.watchMount(function (el) {
   *   el.classList.add('my-class');
   * });
   * @description
   * Allows to subscribe to element initialization and changes.
   * @descriptionRu
   * Позволяет подписаться на инициализацию и изменения DOM элемента.
   */
  public watchMount(handler: MountListener): void {
    if (this.uiEl) {
      handler(this.uiEl)
      return
    }
    this.mountListeners.push(handler)
  }

  /**
   * @code
   * (...).onClick = function (e) {
   *
   * };
   * @description
   * Function that is called when element is clicked.
   * @descriptionRu
   * Функция, которая вызывается когда на элемент
   * кликнули.
   * @example
   * var txt = UI.text('Click me!');
   * txt.onClick = function () {
   *   alert('I was clicked!');
   * };
   */
  public onClick?: (e: MouseEvent) => boolean

  /**
   * @code
   * Items.get('...').onRender = ...
   * @description
   * This function is run when the element is rendered
   * @descriptionRu
   * Эта функция выполняется при отрисовке элемента
   */
  public onRender?: () => void

  /**
   * @todo implement
   */
  public onKeyUp?: (e: MouseEvent) => void

  /**
   * @todo implement
   */
  public onKeyDown?: (e: MouseEvent) => void

  /**
   * @code
   * Items.get('...').onUnmount = ...
   * @description
   * This function is run when the element is removed
   * @descriptionRu
   * Эта функция выполняется при удалении элемента
   */
  public onUnmount?: () => void
  /**
   * @description
   * If true, the element is driven by React and should not be modified by built-in methods.
   * @descriptionRu
   * Если true, то элемент управляется React и не должен изменяться встроенными методами.
   */
  public isDrivenByReact: boolean
}
